/**
 * Port Forwards Tab
 *
 * Manages port forwarding (DNAT) rules on the Cloud Gateway agent.
 * Rules are pass-through: stored on the agent, not in the database.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRightLeftIcon,
  PlusIcon,
  Trash2Icon,
  PencilIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  XIcon,
  CheckCircleIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { gatewayApi } from '@nebula/shared/services'
import type { GatewayPortForwardCreate, GatewayPortForwardUpdate } from '@nebula/shared/services'
import { getApiErrorText } from '@nebula/shared/utils'

/** Validate IP address (no CIDR). Returns error message or null. */
function validateIp(value: string): string | null {
  if (!value) return null
  const pattern = /^(\d{1,3}\.){3}\d{1,3}$/
  if (!pattern.test(value)) return 'Formato inválido. Use um IP válido (ex: 10.0.0.10)'
  for (const octet of value.split('.')) {
    if (Number.parseInt(octet) > 255) return `Octeto inválido: ${octet} (máximo 255)`
  }
  return null
}

export default function PortForwardsTab() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<any>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [gatewayError, setGatewayError] = useState<string | null>(null)

  // Form state
  const [touchedInternalIp, setTouchedInternalIp] = useState(false)
  const [protocol, setProtocol] = useState('tcp')
  const [externalPort, setExternalPort] = useState('')
  const [internalIp, setInternalIp] = useState('')
  const [internalPort, setInternalPort] = useState('')
  const [comment, setComment] = useState('')

  // Port forwards vêm do próprio endpoint (`{port_forwards, total}`), não do ruleset de
  // firewall. Antes, esta aba lia `result.port_forwards` da listagem de firewall — chave que
  // não existe no contrato do backend — e ficava PERMANENTEMENTE vazia, mesmo com regras
  // ativas no gateway. Foi o que levou o usuário a recriar a regra e provocar o 500 (BUG-018).
  const {
    data: pfResult,
    isLoading,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ['gateway-port-forwards'],
    queryFn: gatewayApi.listPortForwards,
  })

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  useEffect(() => {
    if (gatewayError) {
      const timer = setTimeout(() => setGatewayError(null), 10_000)
      return () => clearTimeout(timer)
    }
  }, [gatewayError])

  /**
   * A mutação devolve o RECURSO (não o envelope `{success, result}` do agent). O dual-write
   * grava no banco e tenta aplicar no gateway: se o agent falhar, a resposta vem 200 com
   * `sync_error` preenchido — e dizer "aplicada com sucesso" aí seria mentir (a regra existe
   * no banco e NÃO no nftables). Ver BUG-018.
   */
  const checkGatewayResult = (res: any): { ok: boolean; error?: string } => {
    if (res?.sync_error) {
      return { ok: false, error: res.sync_error }
    }
    // envelope legado (endpoints que ainda respondem `{success, result}`)
    if (res?.success === false) {
      return { ok: false, error: res.error || 'Falha na comunicação com o gateway' }
    }
    const inner = res?.result
    if (inner && typeof inner === 'object' && inner.success === false) {
      return { ok: false, error: inner.error || 'O gateway não conseguiu aplicar a regra' }
    }
    return { ok: true }
  }

  const addMutation = useMutation({
    mutationFn: (data: GatewayPortForwardCreate) => gatewayApi.addPortForward(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-port-forwards'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra aplicada com sucesso')
        resetForm()
      } else {
        setGatewayError(`Erro ao aplicar regra no gateway: ${check.error}`)
      }
    },
  })

  const editMutation = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: GatewayPortForwardUpdate }) =>
      gatewayApi.updatePortForward(ruleId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-port-forwards'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra atualizada com sucesso')
        resetForm()
      } else {
        setGatewayError(`Erro ao atualizar regra no gateway: ${check.error}`)
      }
    },
  })

  const removeMutation = useMutation({
    mutationFn: (ruleId: string) => gatewayApi.removePortForward(ruleId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-port-forwards'] })
      const check = checkGatewayResult(res)
      if (check.ok) {
        setSuccessMessage('Regra removida com sucesso')
      } else {
        setGatewayError(`Erro ao remover regra no gateway: ${check.error}`)
      }
    },
  })

  const resetForm = () => {
    setShowForm(false)
    setEditingRule(null)
    setProtocol('tcp')
    setExternalPort('')
    setInternalIp('')
    setInternalPort('')
    setComment('')
    setTouchedInternalIp(false)
  }

  const startEdit = (pf: any) => {
    setEditingRule(pf)
    setProtocol(pf.protocol || 'tcp')
    setExternalPort(pf.external_port ? String(pf.external_port) : '')
    setInternalIp(pf.internal_ip || '')
    setInternalPort(pf.internal_port ? String(pf.internal_port) : '')
    setComment(pf.comment || '')
    setShowForm(true)
  }

  const internalIpError = validateIp(internalIp)
  const hasValidationErrors = !!internalIpError

  const handleSubmit = () => {
    if (hasValidationErrors) return
    if (editingRule) {
      // Edit mode
      const data: GatewayPortForwardUpdate = {}
      if (protocol !== (editingRule.protocol || '')) data.protocol = protocol
      const extPort = externalPort ? Number.parseInt(externalPort, 10) : undefined
      if (extPort !== editingRule.external_port) data.external_port = extPort
      if (internalIp !== (editingRule.internal_ip || '')) data.internal_ip = internalIp
      const intPort = internalPort ? Number.parseInt(internalPort, 10) : undefined
      if (intPort !== editingRule.internal_port) data.internal_port = intPort
      // `null`, não `undefined`: `undefined` some do JSON, o `exclude_unset` do backend
      // trata como "não enviado" e o agent — que mescla por chave presente — mantém a
      // descrição antiga no gateway, com a tela dizendo "atualizado com sucesso".
      // As portas acima seguem `undefined` de propósito: são obrigatórias, e `null` nelas
      // é 422 (`_recusa_null_em_obrigatorio`).
      if (comment !== (editingRule.comment || '')) data.comment = comment || null

      editMutation.mutate({ ruleId: editingRule.id, data })
    } else {
      // Add mode
      if (!externalPort || !internalIp || !internalPort) return

      const payload: GatewayPortForwardCreate = {
        protocol,
        external_port: Number.parseInt(externalPort, 10),
        internal_ip: internalIp,
        internal_port: Number.parseInt(internalPort, 10),
      }
      if (comment) payload.comment = comment

      addMutation.mutate(payload)
    }
  }

  const isPending = addMutation.isPending || editMutation.isPending
  const isError = addMutation.isError || editMutation.isError
  const error = addMutation.error || editMutation.error

  // Sem cast: `listPortForwards()` já devolve `GatewayPortForward[]`. Manter `any` aqui
  // desligaria o tipo justamente no ponto que este fix trava (o contrato da listagem).
  const portForwards = pfResult?.port_forwards ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center space-x-2">
          <ArrowRightLeftIcon className="size-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Port Forwarding</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            <RefreshCwIcon className={`mr-1.5 size-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700"
          >
            <PlusIcon className="mr-1.5 size-4" />
            Adicionar
          </button>
        </div>
      </div>

      {/* Success feedback */}
      {successMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircleIcon className="size-4 shrink-0 text-green-500" />
          {successMessage}
        </div>
      )}

      {/* Gateway error feedback */}
      {gatewayError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-red-500" />
          <div>
            <p className="font-medium">Falha ao aplicar no gateway</p>
            <p className="mt-1 text-xs">{gatewayError}</p>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              {editingRule ? 'Editar Port Forward' : 'Novo Port Forward'}
            </h4>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <XIcon className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Protocolo</label>
              <select
                value={protocol}
                onChange={(e) => setProtocol(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Porta Externa</label>
              <input
                type="number"
                value={externalPort}
                onChange={(e) => setExternalPort(e.target.value)}
                placeholder="Ex: 8080"
                min={1}
                max={65_535}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">IP Interno</label>
              <input
                type="text"
                value={internalIp}
                onChange={(e) => setInternalIp(e.target.value)}
                onBlur={() => setTouchedInternalIp(true)}
                placeholder="Ex: 10.0.0.10"
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 ${touchedInternalIp && internalIpError ? 'border-red-400' : 'border-gray-300'}`}
              />
              {touchedInternalIp && internalIpError && (
                <p className="mt-1 text-xs text-red-500">{internalIpError}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Porta Interna</label>
              <input
                type="number"
                value={internalPort}
                onChange={(e) => setInternalPort(e.target.value)}
                placeholder="Ex: 80"
                min={1}
                max={65_535}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Comentário</label>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Descrição"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          {isError && (
            <div className="text-sm text-red-600">
              {/* `getApiErrorText`, não `error.message` cru: numa recusa de validação
                  (422) o `message` é sempre o genérico "Request validation failed", e o
                  motivo — "comentário não pode conter aspas duplas" — vive em
                  `details[].msg`. Validar na entrada foi a decisão certa, mas trocou um
                  `sync_error` que a tela EXIBIA por um 422 que ela não sabia ler. */}
              {getApiErrorText(error) || 'Erro ao salvar port forward'}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              // Os obrigatórios valem na EDIÇÃO também. Limpá-los ali produzia `undefined`,
              // o campo sumia do JSON, o `exclude_unset` mantinha o valor antigo e a tela
              // dizia "atualizado com sucesso" sem nada ter mudado — o "aplicado que não
              // está lá" da própria issue, na ponta da UI.
              disabled={
                isPending || hasValidationErrors || !externalPort || !internalIp || !internalPort
              }
              className="inline-flex items-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
            >
              {isPending && <Loader2Icon className="mr-1.5 size-4 animate-spin" />}
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Port Forwards Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-6 animate-spin text-purple-600" />
        </div>
      ) : queryError ? (
        /* A falha de carregamento vem da própria query (o contrato novo não traz `error`). */
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangleIcon className="size-5 shrink-0 text-red-500" />
          <span className="text-sm text-red-700">Não foi possível carregar os port forwards.</span>
        </div>
      ) : portForwards.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <ArrowRightLeftIcon className="mx-auto mb-3 size-10 text-gray-300" />
          <p className="text-sm">Nenhum port forward configurado.</p>
          <p className="mt-1 text-xs text-gray-400">
            Port forwards permitem direcionar tráfego externo para máquinas internas.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Proto
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Porta Externa
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase"></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  IP Interno
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Porta Interna
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Comentário
                </th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {portForwards.map((pf: any, index: number) => (
                <tr
                  key={pf.id || pf.handle || index}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 uppercase">
                      {pf.protocol || 'tcp'}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-900">{pf.external_port}</td>
                  <td className="px-3 py-2 text-center text-gray-400">
                    <ArrowRightLeftIcon className="inline size-4" />
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-900">{pf.internal_ip}</td>
                  <td className="px-3 py-2 font-mono text-gray-900">{pf.internal_port}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{pf.comment || ''}</td>
                  <td className="px-3 py-2 text-right">
                    {(pf.id || pf.handle) && (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(pf)}
                          className="text-gray-400 transition-colors hover:text-purple-600"
                          title="Editar"
                        >
                          <PencilIcon className="size-4" />
                        </button>
                        <button
                          onClick={() => removeMutation.mutate(String(pf.id || pf.handle))}
                          disabled={removeMutation.isPending}
                          className="text-red-400 transition-colors hover:text-red-600"
                          title="Remover"
                        >
                          <Trash2Icon className="size-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
